import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, Ruler } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/sk";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export const openMengder = () => window.dispatchEvent(new Event("canvas:mengder"));
const fmt = (q) => String(q).replace(".", ",");

/** One quiet line where the sum is incomplete because quantities are missing – and the way to fill them all at once. */
export function MengdeBar({ n, className }) {
  if (!n) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[12px] bg-[hsl(var(--v-assumed)/0.08)] px-4 py-2.5 text-[13.5px]", className)} data-testid="mengde-bar">
      <Ruler className="h-4 w-4 shrink-0 text-[hsl(var(--v-assumed))]" />
      <span className="min-w-0 flex-1"><b className="font-semibold">{n} {n === 1 ? "linje mangler" : "linjer mangler"} mengde</b> – derfor står prisen som «mangler» og summen er ikke komplett.</span>
      <button type="button" onClick={openMengder} className="inline-flex h-8 items-center rounded-full bg-[hsl(var(--navy))] px-3.5 text-[13px] font-semibold text-white transition-colors hover:bg-[hsl(var(--ink-2))]" data-testid="mengde-bar-open">Fyll inn mengder</button>
    </div>
  );
}

function Line({ l, value, onChange, onEnter, inputRef, i }) {
  return (
    <li className="py-3" data-testid={`mengde-line-${i}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium leading-snug">{l.description}</div>
          <div className="mt-0.5 text-[12.5px] text-muted-foreground">{l.unit_price != null ? `à ${money(l.unit_price, "NOK", 1, { bare: true })} kr pr ${l.unit || "enhet"}` : "Pris settes i kalkylen"}</div>
        </div>
        <label className="relative w-[118px] shrink-0">
          <input ref={inputRef} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, ""))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(); } }} placeholder="Mengde" className="h-10 w-full rounded-[10px] border border-black/[0.12] bg-white pl-3 pr-10 text-right font-mono text-[14px] tabular-nums outline-none transition-[border-color,box-shadow] focus:border-primary/60 focus:ring-2 focus:ring-primary/15" data-testid={`mengde-input-${i}`} />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12.5px] text-muted-foreground">{l.unit}</span>
        </label>
      </div>
      {l.suggestions?.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {l.suggestions.map((s, k) => <button key={k} type="button" onClick={() => onChange(fmt(s.qty))} className="max-w-full truncate rounded-full bg-[hsl(var(--brand-soft))] px-2.5 py-1 text-left text-[12px] text-foreground/80 transition-colors hover:bg-[hsl(var(--brand-soft)/0.7)]" title={s.label} data-testid={`mengde-suggest-${i}-${k}`}><b className="font-mono font-semibold">{fmt(s.qty)} {l.unit}</b> · {s.label}</button>)}
        </div>
      ) : null}
    </li>
  );
}

/** «Mangler mengde» in one list: every line without a quantity, grouped by post, with the request's own numbers as suggestions. */
export function MengderSheet({ projectId, ops }) {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const refs = useRef({});
  useEffect(() => { const on = () => setOpen(true); window.addEventListener("canvas:mengder", on); return () => window.removeEventListener("canvas:mengder", on); }, []);
  useEffect(() => {
    if (!open) return;
    setLines(null); setVals({});
    api.get(`/projects/${projectId}/mengder`).then((r) => setLines(r.data.lines)).catch((e) => { toast.error(errMsg(e)); setLines([]); });
  }, [open, projectId]);
  const filled = Object.entries(vals).filter(([, v]) => String(v).trim() !== "");
  const save = async () => {
    if (!filled.length || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/mengder`, { values: filled.map(([element_id, qty]) => ({ element_id, qty })) });
      ops.apply(r.data);
      setLines(r.data.lines); setVals({});
      toast.success(`Mengde satt på ${r.data.saved} ${r.data.saved === 1 ? "linje" : "linjer"}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const next = (i) => { const l = (lines || [])[i + 1]; if (l) refs.current[l.element_id]?.focus(); else save(); };
  let lastRef = null;
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px]" data-testid="mengder-sheet">
        <SheetHeader className="border-b border-black/[0.06] px-6 pb-4 pt-6 text-left">
          <SheetTitle className="text-[18px]">Mangler mengde</SheetTitle>
          <SheetDescription className="text-[13.5px] leading-[1.5]">Linjene har pris, men ingen mengde – derfor mangler summen. Forslagene er tall fra kundens tekst; du bekrefter hver mengde. Enter går til neste.</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6">
          {!lines ? <div className="flex h-32 items-center justify-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>
            : !lines.length ? (
              <div className="py-14 text-center" data-testid="mengder-done">
                <p className="inline-flex items-center gap-2 text-[15px] font-medium"><Check className="h-4 w-4 text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> Alle linjer har mengde</p>
                <p className="mt-1 text-[13.5px] text-muted-foreground">Summen er oppdatert i kalkylen og på tilbudet.</p>
                <button type="button" onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("canvas:goto", { detail: { tab: "tilbud" } })); }} className="mt-4 inline-flex h-9 items-center rounded-full bg-[hsl(var(--navy))] px-4 text-[13.5px] font-semibold text-white hover:bg-[hsl(var(--ink-2))]" data-testid="mengder-to-tilbud">Til tilbudet</button>
              </div>
            ) : (
              <ul className="divide-y divide-black/[0.06]" data-testid="mengder-list">
                {lines.map((l, i) => {
                  const head = l.ref !== lastRef;
                  lastRef = l.ref;
                  return (
                    <React.Fragment key={l.element_id}>
                      {head ? <li className="pb-1 pt-5 text-[12.5px] font-semibold text-foreground/80" data-testid={`mengder-post-${l.ref}`}><span className="font-mono text-muted-foreground">{l.ref}</span> {l.item_title}{l.parent_title ? <span className="font-normal text-muted-foreground"> · under {l.parent_title}</span> : null}</li> : null}
                      <Line l={l} i={i} value={vals[l.element_id] || ""} onChange={(v) => setVals((s) => ({ ...s, [l.element_id]: v }))} onEnter={() => next(i)} inputRef={(el) => { refs.current[l.element_id] = el; }} />
                    </React.Fragment>
                  );
                })}
              </ul>
            )}
        </div>
        {lines?.length ? (
          <div className="flex items-center justify-between gap-3 border-t border-black/[0.06] px-6 py-4">
            <span className="text-[13px] text-muted-foreground" data-testid="mengder-count">{filled.length} av {lines.length} fylt inn</span>
            <button type="button" onClick={save} disabled={!filled.length || busy} className="inline-flex h-10 items-center gap-2 rounded-full bg-[hsl(var(--navy))] px-5 text-[14px] font-semibold text-white transition-[background-color,opacity] hover:bg-[hsl(var(--ink-2))] disabled:opacity-40" data-testid="mengder-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Lagre {filled.length || ""} {filled.length === 1 ? "mengde" : "mengder"}</button>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
